import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getPropertyVendorsMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { SupplierLogo } from 'shared/ui/SupplierLogo';

const MAX_LOGOS_TO_DISPLAY = 3;
const MAX_NAMES_TO_DISPLAY = 1;
const LOGO_SIZE = 18;

export interface SupplierBadgeItem {
    uid: string;
    sellerUid: string;
    name: string;
}

interface SupplierBadgeProps {
    proposals: SupplierBadgeItem[];
    maxLogos?: number;
    maxNames?: number;
    opacity?: number;
}

export const SupplierBadge = ({
    proposals,
    maxLogos = MAX_LOGOS_TO_DISPLAY,
    maxNames = MAX_NAMES_TO_DISPLAY,
    opacity = 1,
}: SupplierBadgeProps) => {
    const { t } = useTranslation();
    const vendorsMap = useSelector(getPropertyVendorsMap);

    const logosToDisplay = useMemo(
        () =>
            proposals
                ?.map((proposal, index) => {
                    if (index >= maxLogos) return null;

                    const vendor = vendorsMap?.[proposal.sellerUid];
                    if (!vendor) return null;

                    return (
                        <SupplierLogo
                            key={proposal.uid}
                            vendor={vendor}
                            size={LOGO_SIZE}
                            sx={{ marginLeft: index > 0 ? '-3px' : '0px' }}
                        />
                    );
                })
                .filter(Boolean),
        [proposals, vendorsMap, maxLogos],
    );

    const textToDisplay = useMemo(() => {
        if (proposals?.length === 0) return null;

        if (proposals?.length > maxNames) {
            return `${proposals.length} ${t('suppliers')}`;
        }

        return proposals
            .map((proposal, index) => {
                if (index >= maxNames) return null;
                return proposal.name;
            })
            .filter(Boolean)
            .join(', ');
    }, [proposals, t, maxNames]);

    if (proposals?.length === 0) return null;

    return (
        <Box sx={{ display: 'flex', gap: '9px' }}>
            <Box sx={{ display: 'flex', gap: '0px', opacity }}>
                {logosToDisplay}
            </Box>
            <Box
                sx={{
                    color: '#000',
                    fontSize: '13px',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    opacity,
                }}
            >
                {textToDisplay}
            </Box>
        </Box>
    );
};
